import React, { useCallback, useEffect, useState } from "react";

import { Button, Divider, Header } from "semantic-ui-react";

import { useIntl } from "react-intl";

import DashboardController from "react-lib/apps/IsHealth/Dashboard/DashboardController";
import ModInfoOA from "react-lib/apps/IsHealth/REG/ModInfoOA";
import UserProfileImage from "react-lib/apps/IsHealth/REG/UserProfileImage";

import PRXManager from "react-lib/apis/manager/PRXManager";

import SettingOAAccountFormBasic from "./SettingOAAccountFormBasic";
import SettingOAAccountFormDetailed from "./SettingOAAccountFormDetailed";

import CONFIG from "config/config";

type SettingOAAccountProps = {
  controller: DashboardController & {
    prxManager: PRXManager;
  };
  apiToken: string;
  division: number;
  layout?: "basic" | "detailed";
  onUpdated?: () => void;
};

const HOSPITAL_IMAGE_URL = `${process.env.PUBLIC_URL}/static/images/hospital.png`;

const SettingOAAccount: React.FC<SettingOAAccountProps> = (props) => {
  const intl = useIntl();
  const [profileImageSrc, setProfileImageSrc] = useState(HOSPITAL_IMAGE_URL);
  const [divisionProfile, setDivisionProfile] = useState<any>({});
  const [fileImage, setFileImage] = useState<File | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [healthFacilityTypeOptions, setHealthFacilityTypeOptions] = useState<Record<string, any>[]>(
    []
  );
  const [modInfoMsg, setModInfoMsg] = useState("");

  const getHealthFacilityType = useCallback(async () => {
    if (!props.controller.getHealthFacilityType) {
      return;
    }

    const [types]: [Record<string, any>[]] = await props.controller.getHealthFacilityType({
      apiToken: props.apiToken,
    });

    const options = types.map((item: any) => ({
      key: item.name,
      text: item.name,
      value: item.name,
    }));

    setHealthFacilityTypeOptions(options);
  }, [props.apiToken]);

  // Fetch division profile
  const getOADivisionProfile = useCallback(async () => {
    if (!props.division || !props.apiToken) {
      return;
    }

    setIsLoading(true);

    try {
      const [res] = await props.controller.prxManager.getOADivisionProfile({
        apiToken: props.apiToken,
        divisionId: props.division,
      });

      if (res) {
        setDivisionProfile({
          ...res,
          account_link: res.token
            ? `${CONFIG.OFFICIAL_ACCOUNT_HOST || "https://ishealth.app"}/OA/${
                res.id
              }/?openExternalBrowser=1`
            : "",
        });

        setProfileImageSrc(res.image_file || HOSPITAL_IMAGE_URL);
      }
    } catch (error) {
      console.error("Error fetching division profile:", error);
    } finally {
      setIsLoading(false);
    }
  }, [props.apiToken, props.division]);

  // Handle profile update
  const handleEditProfile = async () => {
    if (!divisionProfile) {
      return;
    }

    setIsLoading(true);

    try {
      const [, error] = await props.controller.prxManager.patchOADivisionProfile({
        apiToken: props.apiToken,
        divisionId: props.division,
        ...divisionProfile,
        divisionName: divisionProfile.name,
        hospitalName: divisionProfile.hospital_name,
        imageFile: fileImage,
        premiumUrl: divisionProfile.premium_url,
        systemName: divisionProfile.system_name,
      });

      if (error) {
        if (props.layout === "detailed") {
          setModInfoMsg("เกิดข้อผิดพลาด");
        }

        console.error("Error editing profile:", error);

        return;
      }

      console.log("Profile updated successfully");

      // Refresh profile data
      await getOADivisionProfile();

      props.onUpdated?.();

      if (props.layout === "detailed") {
        setModInfoMsg("บันทึกสำเร็จ");
      }
    } catch (error) {
      console.error("Unexpected error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangeValue = (e: any, data: any) => {
    setDivisionProfile({
      ...divisionProfile,
      [data.name]: data.value,
    });
  };

  // Handle image change from UserProfileImage component
  const handleImageChange = useCallback((file: File, fileUrl: string) => {
    setFileImage(file);
    setProfileImageSrc(fileUrl);
  }, []);

  // Handle image clear from UserProfileImage component
  const handleClearImage = useCallback(() => {
    setFileImage(null);
    setProfileImageSrc(divisionProfile.image_file || HOSPITAL_IMAGE_URL);
  }, [divisionProfile.image_file]);

  // Load profile on mount and when division changes
  useEffect(() => {
    void getOADivisionProfile();
  }, [getOADivisionProfile]);

  useEffect(() => {
    void getHealthFacilityType();
  }, []);

  return (
    <div style={{ height: "calc(100vh - 40px)", overflowY: "auto", padding: "20px" }}>
      <Header style={{ color: "#4f4f4f" }} as="h2">
        {intl.formatMessage({ id: "ตั้งค่าบัญชี OA" })}
      </Header>
      <Divider style={{ marginBottom: "25px" }} />

      <div className="register-new-user" style={{ height: "auto" }}>
        <UserProfileImage
          src={profileImageSrc}
          onChange={handleImageChange}
          onClear={handleClearImage}
        />

        <div className="vl" />

        {props.layout === "detailed" ? (
          <div className="profile-info" style={{ borderLeft: 0, marginBottom: "3rem" }}>
            <SettingOAAccountFormDetailed
              data={divisionProfile}
              healthFacilityTypeOptions={healthFacilityTypeOptions}
              onChangeValue={handleChangeValue}
            />

            <br />

            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <Button
                style={{ backgroundColor: "var(--primary-theme-color)", color: "white" }}
                disabled={isLoading}
                loading={isLoading}
                type="submit"
                onClick={handleEditProfile}
              >
                บันทึกข้อมูลการแก้ไข
              </Button>
            </div>
          </div>
        ) : (
          <SettingOAAccountFormBasic
            data={divisionProfile}
            isLoading={isLoading}
            onChangeValue={handleChangeValue}
            onSubmit={handleEditProfile}
          />
        )}
      </div>

      <ModInfoOA
        open={!!modInfoMsg}
        type={modInfoMsg.includes("บันทึกสำเร็จ") ? "success" : "error"}
        onApprove={() => {
          setModInfoMsg("");
        }}
        onClose={() => {
          setModInfoMsg("");
        }}
      />
    </div>
  );
};

export default SettingOAAccount;
